/**
 * Credits Ledger Page - Account Information Surface
 * 
 * PURPOSE: Explain Reader Credits as ENTITLEMENTS and CONSUMPTION, not money.
 * 
 * DATA MODEL: Daily Aggregation
 * - Date
 * - Credits Added (sum) - subscription allocation, bundle purchase, promo award
 * - Credits Used (sum) - by book/Audia
 * 
 * EXPLICIT EXCLUSIONS:
 * - No financial amounts (dollars)
 * - No Stripe references
 * - No FTR identifiers
 * 
 * UI LABELING (Required):
 * "Daily Credit Summary — totals are aggregated by day for clarity."
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Coins, Calendar, TrendingUp, TrendingDown, Book, 
  MessageSquare, Gift, RefreshCw, Loader2, ChevronDown, ChevronUp, Info
} from 'lucide-react';
import AccountNav from '../../components/AccountNav';
import UniversalFooter from '../../components/UniversalFooter';
import { useIsReaderRole } from '../../hooks/useIsReaderRole';
import { API_URL } from '@/config/api';

// API_URL imported from centralized config

const CreditsPage = () => {
  const navigate = useNavigate();
  const { user, getHomePage } = useAuth();
  const isReader = useIsReaderRole();
  
  const [loading, setLoading] = useState(true);
  const [creditsData, setCreditsData] = useState([]);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [dateRange, setDateRange] = useState('30');
  const [expandedDays, setExpandedDays] = useState({});

  const userId = user?.user_id || user?.id;
  
  // P0.5: Redirect non-Readers away from Reader Credits page
  // Non-Reader roles should never see RCM surfaces
  useEffect(() => {
    if (user && !isReader) {
      console.log('[CREDITS] Non-Reader role detected, redirecting to profile');
      navigate('/account/profile', { replace: true });
    }
  }, [user, isReader, navigate]);

  // Fetch credits ledger data
  const fetchCreditsData = useCallback(async () => {
    if (!userId) return;
    
    setLoading(true);
    try {
      // Fetch current balance from NFM reader endpoint
      const balanceResponse = await fetch(
        `${API_URL}/api/nfm/reader/balance?user_id=${userId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      );
      
      if (balanceResponse.ok) {
        const balanceData = await balanceResponse.json();
        setCurrentBalance(balanceData.balance || 0);
      }

      // Fetch credits ledger (daily aggregated)
      const ledgerResponse = await fetch(
        `${API_URL}/api/nfm/reader/credits-ledger?user_id=${userId}&days=${dateRange}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      );
      
      if (ledgerResponse.ok) {
        const ledgerData = await ledgerResponse.json();
        setCreditsData(ledgerData.daily_summary || []);
      } else {
        // If endpoint doesn't exist yet, show empty state
        setCreditsData([]);
      }
    } catch (error) {
      console.error('Error fetching credits data:', error);
      setCreditsData([]);
    } finally {
      setLoading(false);
    }
  }, [userId, dateRange]);

  useEffect(() => {
    fetchCreditsData();
  }, [fetchCreditsData]);

  // Toggle day expansion
  const toggleDay = (date) => {
    setExpandedDays(prev => ({
      ...prev,
      [date]: !prev[date]
    }));
  };

  // Format date for display
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get icon for credit source
  const getSourceIcon = (source) => {
    switch (source) {
      case 'subscription':
        return <RefreshCw className="w-4 h-4 text-purple-400" />;
      case 'bundle':
        return <Gift className="w-4 h-4 text-amber-400" />;
      case 'promo':
        return <Gift className="w-4 h-4 text-green-400" />;
      default:
        return <Coins className="w-4 h-4 text-slate-400" />;
    }
  };

  // Get icon for usage type
  const getUsageIcon = (type) => {
    switch (type) {
      case 'playback':
        return <Book className="w-4 h-4 text-blue-400" />;
      case 'audia':
        return <MessageSquare className="w-4 h-4 text-cyan-400" />;
      default:
        return <TrendingDown className="w-4 h-4 text-slate-400" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800">
      <AccountNav />
      
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Reader Credits</h1>
          <p className="text-slate-400">
            Track how you earn and use your Reader Credits
          </p>
        </div>

        {/* Account Navigation Tabs */}
        <div className="mb-6 flex gap-2 border-b border-slate-700 pb-4">
          <Link 
            to="/account/profile"
            className="px-4 py-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-slate-200 transition-colors"
          >
            Profile
          </Link>
          <Link 
            to="/account/credits"
            className="px-4 py-2 rounded-lg bg-amber-500/20 text-amber-400 font-medium"
          >
            Reader Credits
          </Link>
          <Link 
            to="/account/billing"
            className="px-4 py-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-slate-200 transition-colors"
          >
            Billing
          </Link>
          <Link 
            to="/home"
            className="px-4 py-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-slate-200 transition-colors"
          >
            Reader Home
          </Link>
        </div>

        {/* Current Balance Card */}
        <Card className="bg-gradient-to-br from-amber-900/30 to-amber-800/20 border-amber-700/50 mb-6" data-testid="balance-card">
          <CardContent className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-300/80 text-sm mb-1">Current Balance</p>
                <p className="text-4xl font-bold text-amber-400">
                  {currentBalance.toFixed(2)} <span className="text-lg">RC</span>
                </p>
              </div>
              <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Coins className="w-8 h-8 text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Filters */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-slate-400" />
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-[150px] bg-slate-800 border-slate-700 text-slate-100">
                <SelectValue placeholder="Date range" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="7" className="text-slate-100">Last 7 days</SelectItem>
                <SelectItem value="30" className="text-slate-100">Last 30 days</SelectItem>
                <SelectItem value="90" className="text-slate-100">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Info className="w-4 h-4" />
            Daily Credit Summary — totals are aggregated by day for clarity
          </div>
        </div>

        {/* Credits Ledger */}
        <Card className="bg-slate-800/50 border-slate-700" data-testid="credits-ledger">
          <CardHeader>
            <CardTitle className="text-white">Reader Credit Activity</CardTitle>
            <CardDescription className="text-slate-400">
              How you earned and used credits
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
              </div>
            ) : creditsData.length === 0 ? (
              <div className="text-center py-12">
                <Coins className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No credit activity in this period</p>
                <p className="text-sm text-slate-500 mt-2">
                  Credits will appear here as you use NewAudia
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {creditsData.map((day) => (
                  <div 
                    key={day.date}
                    className="border border-slate-700 rounded-lg overflow-hidden"
                  >
                    {/* Day Summary Row */}
                    <button
                      onClick={() => toggleDay(day.date)}
                      className="w-full flex items-center justify-between p-4 hover:bg-slate-700/30 transition-colors text-left"
                      data-testid={`day-row-${day.date}`}
                    >
                      <div className="flex items-center gap-4">
                        <span className="text-slate-300 font-medium w-24">
                          {formatDate(day.date)}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-8">
                        {/* Credits Added */}
                        <div className="text-right">
                          <span className="text-xs text-slate-500 block">Added</span>
                          <span className={`font-semibold ${day.credits_added > 0 ? 'text-green-400' : 'text-slate-500'}`}>
                            {day.credits_added > 0 ? `+${day.credits_added.toFixed(1)}` : '—'}
                          </span>
                        </div>
                        
                        {/* Credits Used */}
                        <div className="text-right">
                          <span className="text-xs text-slate-500 block">Used</span>
                          <span className={`font-semibold ${day.credits_used > 0 ? 'text-red-400' : 'text-slate-500'}`}>
                            {day.credits_used > 0 ? `-${day.credits_used.toFixed(1)}` : '—'}
                          </span>
                        </div>
                        
                        {/* Net Change */}
                        <div className="text-right w-20">
                          <span className="text-xs text-slate-500 block">Net</span>
                          <span className={`font-semibold ${
                            (day.credits_added - day.credits_used) >= 0 
                              ? 'text-green-400' 
                              : 'text-red-400'
                          }`}>
                            {(day.credits_added - day.credits_used) >= 0 ? '+' : ''}
                            {(day.credits_added - day.credits_used).toFixed(1)}
                          </span>
                        </div>
                        
                        {expandedDays[day.date] ? (
                          <ChevronUp className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                    </button>

                    {/* Expanded Details */}
                    {expandedDays[day.date] && (
                      <div className="border-t border-slate-700 bg-slate-800/30 p-4 space-y-4">
                        {/* Sources Breakdown */}
                        {day.sources && day.sources.length > 0 && (
                          <div>
                            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">
                              Credits Added
                            </p>
                            <div className="space-y-2">
                              {day.sources.map((source, idx) => (
                                <div key={idx} className="flex items-center justify-between text-sm">
                                  <div className="flex items-center gap-2">
                                    {getSourceIcon(source.type)}
                                    <span className="text-slate-300">{source.label}</span>
                                  </div>
                                  <span className="text-green-400 font-medium">
                                    +{source.amount.toFixed(1)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Usage Breakdown */}
                        {day.usage && day.usage.length > 0 && (
                          <div>
                            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">
                              Credits Used
                            </p>
                            <div className="space-y-2">
                              {day.usage.map((item, idx) => (
                                <div key={idx} className="flex items-center justify-between text-sm">
                                  <div className="flex items-center gap-2">
                                    {getUsageIcon(item.type)}
                                    <span className="text-slate-300">{item.label}</span>
                                  </div>
                                  <span className="text-red-400 font-medium">
                                    -{item.amount.toFixed(1)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* No details available */}
                        {(!day.sources || day.sources.length === 0) && 
                         (!day.usage || day.usage.length === 0) && (
                          <p className="text-sm text-slate-500 text-center py-2">
                            Detailed breakdown not available
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Note */}
        <div className="mt-6 p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-slate-400 mt-0.5" />
            <div>
              <p className="text-sm text-slate-400">
                <strong className="text-slate-300">About Reader Credits:</strong> Credits are issued 
                monthly for subscriptions and immediately for bundle purchases. Usage is tracked 
                as you listen to audiobooks and interact with Audia.
              </p>
            </div>
          </div>
        </div>
      </div>

      <UniversalFooter />
    </div>
  );
};

export default CreditsPage;
